import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Paper, SvgIcon, Typography } from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SearchBox from "@/components/ui/SearchBox";

export type ProjectsSidebarKey = "all" | "mine" | "editAccess" | "shared" | "archive";

interface LeftSidebarProps {
  readonly headerHeight: number;
  readonly width: number;
  readonly activeKey: ProjectsSidebarKey;
  readonly onSelect: (key: ProjectsSidebarKey) => void;
  readonly searchQuery: string;
  readonly onSearchQueryChange: (value: string) => void;
}

const listItemIconRootSelector = "& .MuiListItemIcon-root" as const;
const typographyRootSelector = "& .MuiTypography-root" as const;
const selectedTypographyRootSelector = "&.Mui-selected .MuiTypography-root" as const;

const navItemSx = {
  borderRadius: 1,
  px: 2,
  py: 1,
  [listItemIconRootSelector]: { minWidth: 32, color: "text.secondary" },
  [typographyRootSelector]: { fontSize: 14, color: "text.secondary" },
  [selectedTypographyRootSelector]: { color: "text.primary" }
} as const;

/** Folder icon with a small edit-pencil inset - denotes projects the user can edit but doesn't own. */
const FolderEditOutlinedIcon = () => (
  <SvgIcon fontSize="small">
    <path d="m9.17 6 2 2H20v10H4V6zM10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8z" />
    <g transform="translate(10 8.5) scale(0.4)">
      <path d="m14.06 9.02.92.92L5.92 19H5v-.92zM17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
    </g>
  </SvgIcon>
);

const LeftSidebar = ({ headerHeight, width, activeKey, onSelect, searchQuery, onSearchQueryChange }: LeftSidebarProps) => (
  <Paper
    elevation={0}
    sx={{
      position: "sticky",
      top: headerHeight,
      height: `calc(100vh - ${headerHeight}px)`,
      width,
      borderRightWidth: 1,
      borderRightStyle: "solid",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}
  >
    <Box sx={{ width: "100%", px: 2, pt: 2 }}>
      <SearchBox
        placeholder="Search projects"
        height={32}
        value={searchQuery}
        onChange={onSearchQueryChange}
        inputProps={{ "aria-label": "Search projects" }}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: "0.46px"
          }
        }}
      />
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, pt: 2 }}>
      <List dense disablePadding>
        <ListItemButton selected={activeKey === "all"} onClick={() => onSelect("all")} sx={navItemSx}>
          <ListItemIcon>
            <GridViewOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={<Typography>All projects</Typography>} />
        </ListItemButton>
        <ListItemButton selected={activeKey === "mine"} onClick={() => onSelect("mine")} sx={navItemSx}>
          <ListItemIcon>
            <FolderOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={<Typography>My projects</Typography>} />
        </ListItemButton>
        <ListItemButton selected={activeKey === "editAccess"} onClick={() => onSelect("editAccess")} sx={navItemSx}>
          <ListItemIcon>
            <FolderEditOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary={<Typography>Edit access</Typography>} />
        </ListItemButton>
        <ListItemButton disabled selected={activeKey === "shared"} onClick={() => onSelect("shared")} sx={navItemSx}>
          <ListItemIcon>
            <FolderSharedOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={<Typography>Shared with me</Typography>} />
        </ListItemButton>
      </List>
      <Divider sx={{ my: 2 }} />
      <List dense disablePadding>
        <ListItemButton selected={activeKey === "archive"} onClick={() => onSelect("archive")} sx={navItemSx}>
          <ListItemIcon>
            <DeleteOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={<Typography>Archive</Typography>} />
        </ListItemButton>
      </List>
    </Box>
  </Paper>
);

export default LeftSidebar;
